import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  CRUNCHBASE_PEOPLE_ROLE_OPTIONS,
  getCrunchbasePeopleRoleLabel,
} from "../helpers/crunchbasePeopleRoles";
import { FUNDING_TYPE_ORDER } from "../utils/fundingStages";
import { MultiSelectionAutocomplete } from "./SelectionAutocomplete";

function incrementCount(map, key, amount = 1) {
  if (!key) return;
  map.set(key, (map.get(key) || 0) + amount);
}

function FilterAccordion({ title, subtitle, defaultExpanded = false, children }) {
  return (
    <Accordion
      disableGutters
      defaultExpanded={defaultExpanded}
      elevation={0}
      sx={{
        border: "1px solid rgba(15, 23, 42, 0.08)",
        borderRadius: "14px !important",
        backgroundColor: "#f8fafc",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 1.25,
          minHeight: 46,
          "& .MuiAccordionSummary-content": {
            my: 0.75,
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#1f2937" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
            {subtitle}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.25 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export default function Filters({
  companies,
  selectedIndustry,
  setSelectedIndustry,
  selectedCountry,
  setSelectedCountry,
  selectedStage,
  setSelectedStage,
  selectedEstimatedRevenueRange,
  setSelectedEstimatedRevenueRange,
  selectedCompanyAge,
  setSelectedCompanyAge,
  selectedPeopleRoles,
  setSelectedPeopleRoles,
  selectedFundingPeriod,
  setSelectedFundingPeriod,
  selectedFundingYear,
  setSelectedFundingYear,
}) {
  const currentYear = new Date().getFullYear();
  const companyAgeOptions = [
    ...new Set(
      companies
        .map((c) => {
          const foundedValue = c["Founded"];
          if (!foundedValue) return "";
          const foundedYear = Number(String(foundedValue).slice(0, 4));
          if (Number.isNaN(foundedYear)) return "";
          const age = currentYear - foundedYear;
          if (age < 0) return "";
          if (age < 3) return "0-3 yrs";
          if (age < 6) return "3-6 yrs";
          if (age < 10) return "6-10 yrs";
          return "10+ yrs";
        })
        .filter(Boolean)
    ),
  ].sort((a, b) => ["0-3 yrs", "3-6 yrs", "6-10 yrs", "10+ yrs"].indexOf(a) - ["0-3 yrs", "3-6 yrs", "6-10 yrs", "10+ yrs"].indexOf(b));

  const industryOptions = [
    ...new Set(
      companies
        .flatMap((c) => c.BucketIndustries || [])
        .filter((x) => typeof x === "string" && x.trim() !== "")
    ),
  ];

  const countryOptions = [
    ...new Set(
      companies
        .map((c) => c["Headquarters Location"])
        .filter((x) => typeof x === "string" && x.trim() !== "")
    ),
  ].sort((a, b) => a.localeCompare(b));

  const stageOptions = [
    ...new Set(
      companies
        .map((c) => c["Investment Stage"])
        .filter((x) => typeof x === "string" && x.trim() !== "")
    ),
  ].sort((a, b) => {
    const aIndex = FUNDING_TYPE_ORDER.indexOf(a);
    const bIndex = FUNDING_TYPE_ORDER.indexOf(b);

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    return a.localeCompare(b);
  });

  const estimatedRevenueRangeOptions = [
    ...new Set(
      companies
        .map((c) => c["Estimated Revenue Range"])
        .filter((x) => typeof x === "string" && x.trim() !== "")
    ),
  ].sort((a, b) => a.localeCompare(b));

  const peopleRoleOptions = CRUNCHBASE_PEOPLE_ROLE_OPTIONS.map((option) => option.label);
  const industryCounts = new Map();
  const countryCounts = new Map();
  const stageCounts = new Map();
  const estimatedRevenueRangeCounts = new Map();
  const companyAgeCounts = new Map();
  const peopleRoleCounts = new Map();
  const fundingPeriodCounts = new Map();
  const fundingYearCounts = new Map();

  companies.forEach((company) => {
    (company.BucketIndustries || []).forEach((industry) => incrementCount(industryCounts, industry));
    incrementCount(countryCounts, company["Headquarters Location"]);
    incrementCount(stageCounts, company["Investment Stage"]);
    incrementCount(estimatedRevenueRangeCounts, company["Estimated Revenue Range"]);

    const foundedValue = company["Founded"];
    if (foundedValue) {
      const foundedYear = Number(String(foundedValue).slice(0, 4));
      if (!Number.isNaN(foundedYear)) {
        const age = currentYear - foundedYear;
        if (age >= 0) {
          incrementCount(
            companyAgeCounts,
            age < 3 ? "0-3 yrs" : age < 6 ? "3-6 yrs" : age < 10 ? "6-10 yrs" : "10+ yrs"
          );
        }
      }
    }

    if (!company.hasPeopleData) {
      incrementCount(peopleRoleCounts, getCrunchbasePeopleRoleLabel("no_people_data"));
    }
    (company["People Role Buckets"] || []).forEach((roleValue) => {
      incrementCount(peopleRoleCounts, getCrunchbasePeopleRoleLabel(roleValue));
    });

    incrementCount(fundingPeriodCounts, company["Funding Quarter"]);
    incrementCount(fundingPeriodCounts, company["Funding Month"]);
    incrementCount(fundingYearCounts, company["Funding Year"]);
  });

  const quarterOptions = ["Q1", "Q2", "Q3", "Q4"].filter((quarter) =>
    companies.some((c) => c["Funding Quarter"] === quarter)
  );

  const monthOrder = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const monthOptions = monthOrder.filter((month) =>
    companies.some((c) => c["Funding Month"] === month)
  );

  const yearOptions = [
    ...new Set(
      companies
        .map((c) => c["Funding Year"])
        .filter((x) => typeof x === "string" && x.trim() !== "")
    ),
  ].sort((a, b) => Number(b) - Number(a));

  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      <FilterAccordion
        title="Core Filters"
        subtitle="Industry, geography, and stage"
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 1.25,
            width: "100%",
          }}
        >
          <MultiSelectionAutocomplete
            label="Industry"
            values={selectedIndustry}
            onChange={setSelectedIndustry}
            options={industryOptions}
            counts={industryCounts}
            showCheckboxes
          />
          <MultiSelectionAutocomplete
            label="Country"
            values={selectedCountry}
            onChange={setSelectedCountry}
            options={countryOptions}
            counts={countryCounts}
            showCheckboxes
          />
          <MultiSelectionAutocomplete
            label="Investment Stage"
            values={selectedStage}
            onChange={setSelectedStage}
            options={stageOptions}
            counts={stageCounts}
            showCheckboxes
          />
        </Box>
      </FilterAccordion>

      <FilterAccordion
        title="Additional Filters"
        subtitle="Revenue, maturity, and people roles"
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 1.25,
            width: "100%",
          }}
        >
          <MultiSelectionAutocomplete
            label="Estimated Revenue Range"
            values={selectedEstimatedRevenueRange}
            onChange={setSelectedEstimatedRevenueRange}
            options={estimatedRevenueRangeOptions}
            counts={estimatedRevenueRangeCounts}
            showCheckboxes
          />
          <MultiSelectionAutocomplete
            label="Company Age"
            values={selectedCompanyAge}
            onChange={setSelectedCompanyAge}
            options={companyAgeOptions}
            counts={companyAgeCounts}
            showCheckboxes
          />
          <MultiSelectionAutocomplete
            label="People"
            values={selectedPeopleRoles}
            onChange={setSelectedPeopleRoles}
            options={peopleRoleOptions}
            counts={peopleRoleCounts}
            showCheckboxes
          />
        </Box>
      </FilterAccordion>

      <FilterAccordion
        title="Time Filters"
        subtitle="Funding period and year"
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 1.25,
            width: "100%",
          }}
        >
          <MultiSelectionAutocomplete
            label="Time Period"
            values={selectedFundingPeriod}
            onChange={setSelectedFundingPeriod}
            options={[...quarterOptions, ...monthOptions]}
            counts={fundingPeriodCounts}
            showCheckboxes
          />
          <MultiSelectionAutocomplete
            label="Year"
            values={selectedFundingYear}
            onChange={setSelectedFundingYear}
            options={yearOptions}
            counts={fundingYearCounts}
            showCheckboxes
          />
        </Box>
      </FilterAccordion>
    </Box>
  );
}
